/**
 * useRisikoprofil — TanStack Query hooks for the Kunde-Ebene «Risikoprofil» tab (P2 Slice 1).
 *
 * Server state = TanStack Query ONLY (coding-standards §3). live mode hits the real endpoints
 * via apiClient.customers.*; mock mode resolves catalog-derived fixtures so the tab renders
 * standalone (no backend). The server owns every decision (validation, gating, ampel) — the UI
 * only triggers + renders.
 *
 * [Source: apps/web/src/features/entity-detail/PolicyAvbSection.tsx] (useQuery/useMutation idiom)
 * [Source: apps/web/src/features/entity-detail/hooks/useEntityDetail.ts] (live/mock + qk pattern)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import {
  basisspartenFuer,
  deriveBasisspartenAmpel,
  igb2bFamilieLabel,
  igb2bFamilyOf,
  risikoprofilFelderFuer,
  risikoprofilTypFuerSubject,
  schadenverlaufPeriode,
  IGB2B_ALL_CODES,
} from '@brokly/shared';
import type {
  BasisspartenResponse,
  BasisspartePutBody,
  KundeSchadenSparteItem,
  KundeSchadenSpartePutBody,
  KundeSchadenverlaufResponse,
  RisikoprofilPatchBody,
  RisikoprofilResponse,
} from '@brokly/shared';

type SubjectType = 'person' | 'firma';

// ── Mock fixtures (mock mode only) ──────────────────────────────────────────────

function mockProfile(subjectType: SubjectType, subjectId: string): RisikoprofilResponse {
  const profileType = risikoprofilTypFuerSubject(subjectType);
  return {
    subject_type: subjectType,
    subject_id: subjectId,
    profile_type: profileType,
    attributes: {},
    felder: [...risikoprofilFelderFuer(profileType)],
    gated_fields_suppressed: [],
    stammdaten_mirror:
      subjectType === 'firma'
        ? { rechtsform: null, gruendungsjahr: null, noga_code: null, zweckbeschreibung: null }
        : null,
    derived: {
      anzahl_aktiver_policen: 0,
      anzahl_abgedeckter_sparten: 0,
      igb2b_hauptbranchen: [],
      schadenquote: null,
      schadenfrequenz: null,
      gesamtpraemienvolumen: null,
      deckungsluecken_index: null,
      underinsurance_score: null,
      praemienkonzentration_groesste_police: null,
      mutationsrate_renewal_historie: null,
    },
    einwilligung: { erfasst: false, datum: null },
    data_quality_score: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    updated_at: new Date(0).toISOString(),
  };
}

function mockBaseline(subjectType: SubjectType, subjectId: string): BasisspartenResponse {
  const profileType = risikoprofilTypFuerSubject(subjectType);
  return {
    subject_type: subjectType,
    subject_id: subjectId,
    profile_type: profileType,
    items: basisspartenFuer(profileType).map((def) => ({
      sparte_key: def.sparte_key,
      branchencode: def.branchencode,
      label: def.label,
      default_status: def.default_status,
      kriterium: def.kriterium,
      richtwert: def.richtwert,
      applicable: null,
      status_override: null,
      notiz: null,
      ampel: deriveBasisspartenAmpel({
        default_status: def.default_status,
        applicable: null,
        status_override: null,
        hasPolicy: false,
      }),
      policy_ids: [],
    })),
  };
}

function mockClaimsHistory(subjectType: SubjectType, subjectId: string): KundeSchadenverlaufResponse {
  const profileType = risikoprofilTypFuerSubject(subjectType);
  const periode = schadenverlaufPeriode(new Date());
  return {
    subject_type: subjectType,
    subject_id: subjectId,
    profile_type: profileType,
    zeitraum_default: { von: periode.von, bis: periode.bis },
    items: [],
    legacy_aggregate: null,
    sparten_katalog: IGB2B_ALL_CODES.map((code) => ({
      branchencode: code,
      label: code,
      familie: igb2bFamilyOf(code),
      familie_label: igb2bFamilieLabel(code),
    })),
  };
}

// ── Queries ──────────────────────────────────────────────────────────────────────

export function useRisikoprofil(subjectType: SubjectType, subjectId: string) {
  const live = isLiveData();
  return useQuery<RisikoprofilResponse>({
    queryKey: qk.riskProfile(subjectType, subjectId, live ? 'live' : 'mock'),
    queryFn: () =>
      live
        ? apiClient.customers.getRiskProfile(subjectType, subjectId)
        : Promise.resolve(mockProfile(subjectType, subjectId)),
    ...(live ? {} : { initialData: () => mockProfile(subjectType, subjectId), staleTime: Infinity }),
  });
}

export function useCoverageBaseline(subjectType: SubjectType, subjectId: string) {
  const live = isLiveData();
  return useQuery<BasisspartenResponse>({
    queryKey: qk.coverageBaseline(subjectType, subjectId, live ? 'live' : 'mock'),
    queryFn: () =>
      live
        ? apiClient.customers.getCoverageBaseline(subjectType, subjectId)
        : Promise.resolve(mockBaseline(subjectType, subjectId)),
    ...(live ? {} : { initialData: () => mockBaseline(subjectType, subjectId), staleTime: Infinity }),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export function useRisikoprofilPatch(subjectType: SubjectType, subjectId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: (body: RisikoprofilPatchBody) =>
      live
        ? apiClient.customers.patchRiskProfile(subjectType, subjectId, body)
        : Promise.resolve(mockProfile(subjectType, subjectId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.riskProfile(subjectType, subjectId, live ? 'live' : 'mock') });
    },
  });
}

export function useRisikoprofilReview(subjectType: SubjectType, subjectId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: () =>
      live
        ? apiClient.customers.reviewRiskProfile(subjectType, subjectId)
        : Promise.resolve(mockProfile(subjectType, subjectId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.riskProfile(subjectType, subjectId, live ? 'live' : 'mock') });
    },
  });
}

export function useRisikoprofilEinwilligung(subjectType: SubjectType, subjectId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: (erfasst: boolean) =>
      live
        ? apiClient.customers.putRiskProfileEinwilligung(subjectType, subjectId, erfasst)
        : Promise.resolve(mockProfile(subjectType, subjectId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.riskProfile(subjectType, subjectId, live ? 'live' : 'mock') });
    },
  });
}

export function useCoverageBaselinePut(subjectType: SubjectType, subjectId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: ({ sparteKey, body }: { sparteKey: string; body: BasisspartePutBody }) =>
      live
        ? apiClient.customers.putCoverageBaseline(subjectType, subjectId, sparteKey, body)
        : Promise.resolve(
            mockBaseline(subjectType, subjectId).items.find((i) => i.sparte_key === sparteKey)!,
          ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.coverageBaseline(subjectType, subjectId, live ? 'live' : 'mock') });
    },
  });
}

// ── Schadenverlauf je Sparte (P2 hardening Q15) ──────────────────────────────────

export function useKundeSchadenverlauf(subjectType: SubjectType, subjectId: string) {
  const live = isLiveData();
  return useQuery<KundeSchadenverlaufResponse>({
    queryKey: qk.customerClaimsHistory(subjectType, subjectId, live ? 'live' : 'mock'),
    queryFn: () =>
      live
        ? apiClient.customers.getClaimsHistory(subjectType, subjectId)
        : Promise.resolve(mockClaimsHistory(subjectType, subjectId)),
    ...(live ? {} : { initialData: () => mockClaimsHistory(subjectType, subjectId), staleTime: Infinity }),
  });
}

/** Both the claims-history list AND the risk profile are invalidated (legacy clear changes attributes). */
function invalidateClaims(qc: ReturnType<typeof useQueryClient>, subjectType: SubjectType, subjectId: string, live: boolean) {
  const mode = live ? 'live' : 'mock';
  void qc.invalidateQueries({ queryKey: qk.customerClaimsHistory(subjectType, subjectId, mode) });
  void qc.invalidateQueries({ queryKey: qk.riskProfile(subjectType, subjectId, mode) });
}

export function useKundeSchadenSpartePut(subjectType: SubjectType, subjectId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: ({ branchencode, body }: { branchencode: string; body: KundeSchadenSpartePutBody }): Promise<KundeSchadenSparteItem> =>
      live
        ? apiClient.customers.putClaimsHistorySparte(subjectType, subjectId, branchencode, body)
        : Promise.resolve({ branchencode, label: branchencode, familie: igb2bFamilyOf(branchencode), row: null, auskunft: null }),
    onSuccess: () => invalidateClaims(qc, subjectType, subjectId, live),
  });
}

export function useKundeSchadenSparteRemove(subjectType: SubjectType, subjectId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: (branchencode: string): Promise<void> =>
      live ? apiClient.customers.removeClaimsHistorySparte(subjectType, subjectId, branchencode) : Promise.resolve(),
    onSuccess: () => invalidateClaims(qc, subjectType, subjectId, live),
  });
}

export function useKundeSchadenLegacyClear(subjectType: SubjectType, subjectId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: (): Promise<void> =>
      live ? apiClient.customers.clearClaimsHistoryLegacy(subjectType, subjectId) : Promise.resolve(),
    onSuccess: () => invalidateClaims(qc, subjectType, subjectId, live),
  });
}
